import React, { useState, useEffect } from 'react';
import { log } from '../../services/production-logger';

import {
  Modal,
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Animated,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import useTheme from '../../hooks/useTheme';

interface DeletionProgressData {
  visible: boolean;
  title: string;
  message?: string;
  progress?: {
    total: number;
    deleted: number;
    currentCollection: string;
  };
}

class DeletionProgressManager {
  private static instance: DeletionProgressManager;
  private showProgress?: (data: DeletionProgressData) => void;
  private hideProgress?: () => void;

  static getInstance(): DeletionProgressManager {
    if (!DeletionProgressManager.instance) {
      DeletionProgressManager.instance = new DeletionProgressManager();
    }
    return DeletionProgressManager.instance;
  }

  setHandlers(
    showProgress: (data: DeletionProgressData) => void,
    hideProgress: () => void
  ) {
    this.showProgress = showProgress;
    this.hideProgress = hideProgress;
  }

  show(title: string, message?: string) {
    log.debug('[DeletionProgressManager] show called with:', { title, message });
    if (this.showProgress) {
      log.debug('[DeletionProgressManager] Calling showProgress handler');
      this.showProgress({ visible: true, title, message });
    } else {
      log.warn('[DeletionProgressManager] No showProgress handler set!');
    }
  }

  updateProgress(progress: { total: number; deleted: number; currentCollection: string }) {
    if (this.showProgress) {
      this.showProgress({ 
        visible: true, 
        title: 'Deleting Data...', 
        progress 
      });
    }
  }

  hide() {
    if (this.hideProgress) {
      this.hideProgress();
    }
  }
}

export const DeletionProgress = {
  show: (title: string, message?: string) => {
    DeletionProgressManager.getInstance().show(title, message);
  },
  updateProgress: (progress: { total: number; deleted: number; currentCollection: string }) => {
    DeletionProgressManager.getInstance().updateProgress(progress);
  },
  hide: () => {
    DeletionProgressManager.getInstance().hide();
  },
};

export const DeletionProgressModal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colors, isDark } = useTheme();
  const [data, setData] = useState<DeletionProgressData>({ visible: false, title: '' });
  const fadeAnim = useState(new Animated.Value(0))[0];
  const scaleAnim = useState(new Animated.Value(0.8))[0];
  const spinValue = useState(new Animated.Value(0))[0];

  useEffect(() => {
    log.debug('[DeletionProgressModal] Setting handlers');
    DeletionProgressManager.getInstance().setHandlers(
      (newData: DeletionProgressData) => {
        log.debug('[DeletionProgressModal] Received data update:', newData);
        setData(newData);
      },
      () => {
        log.debug('[DeletionProgressModal] Hide called');
        setData({ visible: false, title: '' });
      }
    );
  }, []);

  useEffect(() => {
    if (data.visible) {
      // Start animations
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 40,
          useNativeDriver: true,
        }),
      ]).start();

      // Start spinner rotation
      Animated.loop(
        Animated.timing(spinValue, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        })
      ).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 0.8,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [data.visible]);

  if (!data.visible) {
    return <>{children}</>;
  }

  const spin = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  const progressPercentage = data.progress 
    ? Math.round((data.progress.deleted / data.progress.total) * 100)
    : 0;

  return (
    <>
      {children}
      <Modal
        transparent
        visible={data.visible}
        animationType="none"
        statusBarTranslucent
      >
        <View style={styles.overlay}>
          {Platform.OS === 'ios' && (
            <BlurView intensity={30} tint={isDark ? 'dark' : 'light'} style={StyleSheet.absoluteFillObject} />
          )}
          {Platform.OS !== 'ios' && (
            <Animated.View
              style={[
                StyleSheet.absoluteFillObject,
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  opacity: fadeAnim,
                },
              ]}
            />
          )}
          <Animated.View
            style={[
              styles.modalContainer,
              {
                backgroundColor: colors.surface,
                opacity: fadeAnim,
                transform: [{ scale: scaleAnim }],
                shadowColor: colors.text,
              },
            ]}
          >
            {/* Animated Icon */}
            <View style={[styles.iconContainer, { backgroundColor: colors.background }]}>
              {data.progress ? (
                <View style={styles.progressIconContainer}>
                  <Ionicons name="trash-outline" size={32} color={colors.error} />
                  <Text style={[styles.progressPercentage, { color: colors.error }]}>
                    {progressPercentage}%
                  </Text>
                </View>
              ) : (
                <Animated.View style={{ transform: [{ rotate: spin }] }}>
                  <Ionicons name="sync-outline" size={48} color={colors.primary} />
                </Animated.View>
              )}
            </View>

            {/* Title */}
            <Text style={[styles.title, { color: colors.text }]}>{data.title}</Text>

            {/* Progress Information */}
            {data.progress ? (
              <View style={styles.progressContainer}>
                <View style={[styles.progressBar, { backgroundColor: colors.border }]}>
                  <View
                    style={[
                      styles.progressFill,
                      {
                        backgroundColor: colors.error,
                        width: `${progressPercentage}%`,
                      },
                    ]}
                  />
                </View>
                <Text style={[styles.progressText, { color: colors.textSecondary }]}>
                  {data.progress.currentCollection}
                </Text>
                <Text style={[styles.progressCount, { color: colors.textSecondary }]}>
                  {data.progress.deleted} of {data.progress.total} items deleted
                </Text>
              </View>
            ) : (
              <>
                {data.message && (
                  <Text style={[styles.message, { color: colors.textSecondary }]}>
                    {data.message}
                  </Text>
                )}
                <ActivityIndicator 
                  size="large" 
                  color={colors.primary} 
                  style={styles.loader}
                />
              </>
            )}

            {/* Warning */}
            <View style={[styles.warningContainer, { backgroundColor: colors.background }]}>
              <Ionicons name="information-circle" size={20} color={colors.warning} />
              <Text style={[styles.warningText, { color: colors.textSecondary }]}>
                Please don't close the app during this process
              </Text>
            </View>
          </Animated.View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 380,
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  progressIconContainer: {
    alignItems: 'center',
  },
  progressPercentage: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 4,
  },
  title: {
    fontSize: 22,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  loader: {
    marginTop: 8,
    marginBottom: 24,
  },
  progressContainer: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 24,
  },
  progressBar: {
    width: '100%',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 12,
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 14,
    marginBottom: 4,
  },
  progressCount: {
    fontSize: 13,
  },
  warningContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    width: '100%',
  },
  warningText: {
    fontSize: 13,
    marginLeft: 8,
    flex: 1,
  },
});

export default DeletionProgressModal;
